import { Text } from '@/components/ui/text';
import { canHandOff } from '@/utils/maps';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Pressable, View } from 'react-native';
import type { Business } from '@/types/business';
import { LocationMap } from './location-map';

interface BusinessLocationSectionProps {
  business: Business;
  onMapsPress: () => void;
}

export function BusinessLocationSection({ business, onMapsPress }: BusinessLocationSectionProps) {
  const hasAddress = business.street || business.city || business.state || business.postalCode;
  const hasCoords = typeof business.latitude === 'number' && typeof business.longitude === 'number';

  if (!hasAddress && !hasCoords) return null;

  return (
    <View>
      <Text className="text-2xl font-semibold mb-3 text-foreground" accessibilityRole="header">
        Location
      </Text>

      <View className="mb-4 rounded-lg overflow-hidden bg-surface">
        {hasCoords && (
          <LocationMap
            key={business.id}
            businessId={business.id}
            businessName={business.name}
            version={business.updatedAt}
            onPress={onMapsPress}
          />
        )}

        <View className="p-4">
          {hasAddress && (
            <View className="flex-row items-start mb-3">
              <Ionicons name="location-outline" size={20} className="text-muted-foreground" style={{ marginTop: 2, marginRight: 12 }} />
              <View className="flex-1">
                {business.street && (
                  <Text className="text-base mb-1 text-foreground">
                    {business.street}
                  </Text>
                )}
                {(business.city || business.state || business.postalCode) && (
                  <Text className="text-sm text-muted-foreground">
                    {[business.city, business.state, business.postalCode].filter(Boolean).join(', ')}
                  </Text>
                )}
              </View>
            </View>
          )}

          {canHandOff(business) && (
            <Pressable
              testID="open-in-maps"
              onPress={onMapsPress}
              accessibilityRole="button"
              className="flex-row items-center justify-center p-3 rounded-lg mt-2 bg-surface border border-border-visible"
            >
              <Ionicons name="map-outline" size={18} className="text-foreground" style={{ marginRight: 8 }} />
              <Text className="text-sm font-semibold text-foreground">
                Open in Maps
              </Text>
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
}
